import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { CompanyService } from '../company.services';
import type { Reseller } from '../company.types';
import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { resellerSettingValidationSchema } from '../company.validations';
import type { InferType } from 'yup';
import { toast } from 'sonner';
import { useAuthStore } from '@/features/auth/auth.store';
import { useFormGuardStore } from '@/stores/form-guard.store';
import { getPageSize } from '@/stores/page-size.store';
import { useUrlFilters, listPathWithFilters } from '@/hooks/use-url-filters';

const LIST_ROUTE = '/setting/company/reseller-setting';

const useResellerList = () => {
  const companyId = useAuthStore(state => state.user?.company?.id ?? '');

  const initialFilters: Reseller.IResellerFilters = {
    search: '',
    page: 1,
    limit: getPageSize('setting.reseller'),
    sort: '',
    order: '',
    status: 'active',
    companyId: companyId,
    name: '',
    contactPerson: '',
    email: '',
    phoneNumber: '',
  };

  const [filters, setFilters] =
    useUrlFilters<Reseller.IResellerFilters>(initialFilters);

  const cleanFilters = useMemo(() => {
    const cleaned: Record<string, string | number> = {};

    // Only include non-empty values
    (
      Object.entries(filters) as [
        keyof Reseller.IResellerFilters,
        string | number | undefined,
      ][]
    ).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });

    // Always include page and limit
    if (filters.page !== undefined) cleaned.page = filters.page;
    if (filters.limit !== undefined) cleaned.limit = filters.limit;

    return cleaned;
  }, [filters]);

  const { data, isLoading } = useQuery({
    queryKey: ['reseller-setting-list', cleanFilters],
    queryFn: CompanyService.getResellerList,
  });

  const searchRef = useRef<HTMLInputElement | null>(null);

  const handleResetFilter = () => {
    if (searchRef.current) searchRef.current.value = '';
    setFilters(initialFilters);
  };

  return { filters, setFilters, data, isLoading, searchRef, handleResetFilter };
};

const useResellerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const setIgnoreGuard = useFormGuardStore(state => state.setIgnoreGuard);
  const companyId = useAuthStore(state => state.user?.company?.id ?? '');

  const form = useForm({
    resolver: yupResolver(resellerSettingValidationSchema),
    defaultValues: {
      status: 'active',
      companyId: companyId,
      branchId: [] as string[],
    },
    mode: 'onChange',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['reseller-setting', id],
    queryFn: CompanyService.getResellerById,
    enabled: !!id,
    select: data => data?.data,
  });

  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: CompanyService.createReseller,
  });
  const editMutation = useMutation({
    mutationFn: CompanyService.editReseller,
  });
  const deleteMutation = useMutation({
    mutationFn: CompanyService.editReseller,
  });

  const onSubmit = (
    data: InferType<typeof resellerSettingValidationSchema>
  ) => {
    if (id) {
      editActivity(data);
    } else {
      createActivity(data);
    }
  };

  const createActivity = (
    data: InferType<typeof resellerSettingValidationSchema>
  ) => {
    createMutation.mutate(data, {
      onSuccess: res => {
        setIgnoreGuard(true);
        queryClient.invalidateQueries();
        toast.success(res.meta.message);
        navigate(listPathWithFilters(LIST_ROUTE));
      },
      onError: err => toast.error(err.message),
    });
  };

  const editActivity = (
    data: InferType<typeof resellerSettingValidationSchema>
  ) => {
    editMutation.mutate(
      { id: id ?? '', data },
      {
        onSuccess: res => {
          setIgnoreGuard(true);
          queryClient.invalidateQueries();
          toast.success(res.meta.message);
          navigate(listPathWithFilters(LIST_ROUTE));
        },
        onError: err => toast.error(err.message),
      }
    );
  };

  const handleReset = () => {
    form.reset({
      ...data,
      status: data?.status ?? 'active',
    });
  };

  const handleDelete = () => {
    if (!id) return;

    const currentData = form.getValues();
    deleteMutation.mutate(
      {
        id,
        data: {
          ...currentData,
          status: 'suspended',
        },
      },
      {
        onSuccess: () => {
          setIgnoreGuard(true);
          queryClient.invalidateQueries();
          toast.success('Reseller deleted successfully');
          navigate(listPathWithFilters(LIST_ROUTE));
        },
        onError: err => toast.error(err.message),
      }
    );
  };

  const handleBack = () => navigate(listPathWithFilters(LIST_ROUTE));

  useEffect(() => {
    if (data) {
      form.reset({
        ...data,
        branchId: data.branchId ?? [],
        status: data.status ?? 'active',
      });
    }
  }, [data, form, id]);

  return {
    id,
    form,
    isLoading,
    createMutation,
    editMutation,
    deleteMutation,
    handleBack,
    handleReset,
    handleDelete,
    onSubmit,
  };
};

export { useResellerList, useResellerForm };
